import { WORLD } from '../../config/tuning';
import { SimplexNoise, distanceToPolyline, lerp, smoothstep } from './Noise';

/**
 * The Kerani theatre (SPEC §3.9.1): a 24 × 24 km coastal landmass with a mountain spine, a bay,
 * a sand spit across the bay mouth, and a river valley running down to the port.
 *
 * The shape is deliberately *authored* rather than left to noise. A pure noise field produces
 * terrain with no landmarks, and a mission built on "fly up the valley, break left at the spit"
 * needs those features to exist as places a player can recognise and navigate by. So the
 * coastline, the spine and the river are polylines with intent, and the noise is what makes them
 * look like geology instead of like a diagram.
 *
 * All coordinates are world metres, origin at the theatre centre, +x east, −z north, y up.
 */

export const THEATRE = {
  seed: 0x4b45_5241, // 'KERA'
  minHeightM: -220,
  maxHeightM: WORLD.maxAltitudeM,

  /**
   * Vertical quantisation of the committed heightfield, metres. Sized against what the renderer
   * can actually represent — the GPU holds heights as normalised half-floats, whose precision at
   * the top of the range is coarser than this — so the extra bits a finer step would cost buy
   * nothing but download size.
   */
  heightStepM: 0.4,

  /** Sea level. Everything below is bathymetry. */
  seaLevelM: 0,

  /** Continental shelf: how far out, and how deep it goes. */
  shelfWidthM: 5200,
  shelfDepthM: 190,

  /** Coastal plain behind the beach. */
  beachWidthM: 140,
  plainWidthM: 2300,
  plainHeightM: 62,

  /** Mountain spine. */
  spineHalfWidthM: 4200,
  spineHeightM: 1400,
  /**
   * Octaves of the ridged spine. Capped so the finest octave stays around 90 m — well above the
   * 11.7 m sample spacing. Finer octaves cannot be represented by the grid: they alias into it
   * as noise, which both degrades the silhouette and costs a great deal of file size to store.
   */
  spineOctaves: 6,

  /** River valley cut down to the bay. */
  riverHalfWidthM: 340,
  riverDepthM: 95,

  /** Sand spit across the bay mouth. */
  spitHalfWidthM: 190,
  spitHeightM: 9,

  /** Port town: a flattened shelf on the bay's north shore. */
  townCentre: [4250, 800] as const,
  townRadiusM: 900,
  townHeightM: 26,

  /** The theatre is an island group: land fades to sea over this margin at the border. */
  borderFadeM: 2600,
} as const;

/**
 * The coastline, north to south. The land lies east of it; the deep indentation at z ≈ 1500 is
 * Kerani Bay, and the port sits on its northern shore.
 */
const COASTLINE: readonly (readonly [number, number])[] = [
  [-1400, -12000],
  [-900, -9200],
  [-200, -6400],
  [400, -3800],
  [900, -1500],
  [1500, 200],
  [4600, 1150], // the bay mouth opens here
  [5100, 2100],
  [4300, 3000],
  [1700, 3300],
  [1200, 5200],
  [1500, 7600],
  [2400, 10000],
  [2900, 12000],
];

/** The mountain spine, running the length of the theatre inland of the coast. */
const SPINE: readonly (readonly [number, number])[] = [
  [8600, -12000],
  [8000, -8000],
  [9100, -4200],
  [9400, -300],
  [8200, 3400],
  [7300, 7000],
  [7900, 11000],
];

/** The river, from a saddle in the spine down to the head of the bay. */
const RIVER: readonly (readonly [number, number])[] = [
  [8300, 1900],
  [7000, 1750],
  [5800, 2050],
  [4600, 1900],
  [3600, 1600],
  [2600, 1500],
];

/** The sand spit, reaching south across the bay mouth from the northern headland. */
const SPIT: readonly (readonly [number, number])[] = [
  [4750, 1250],
  [4500, 1900],
  [4150, 2500],
  [3600, 2850],
];

/**
 * Signed distance east of the coastline, metres. Positive is inland.
 *
 * The polyline gives the large shape; a low-frequency noise term wanders the actual waterline
 * either side of it so the coast has coves and headlands rather than reading as a spline.
 */
function inlandDistanceM(x: number, z: number, noise: SimplexNoise): number {
  return smoothInlandDistanceM(x, z, noise) + noise.fbm(x * 0.0013, z * 0.0013, 2) * 130;
}

/**
 * The same field without the fine detail term.
 *
 * Bathymetry is built from this rather than from the full field, and the reason is compression
 * rather than geology. The seabed is 64% of the theatre and is never seen — it exists only to
 * drive the water's depth colour ramp — but every metre of ripple in it is a residual the
 * encoder has to store. Measured, the fully-detailed seabed cost 46% of the entire heightfield,
 * more than four times what the mountains cost. Smoothing what nobody can look at is free.
 */
function smoothInlandDistanceM(x: number, z: number, noise: SimplexNoise): number {
  const distance = distanceToPolyline(x, z, COASTLINE);
  const inland = isInland(x, z) ? distance : -distance;
  const wander = noise.warpedFbm(x * 0.00021, z * 0.00021, 4, 1.4) * 620;
  return inland + wander;
}

/** East of the coastline polyline at this z. */
function isInland(x: number, z: number): boolean {
  // Walk the polyline to the bracketing segment and compare against its x at this z.
  for (let i = 0; i < COASTLINE.length - 1; i += 1) {
    const a = COASTLINE[i];
    const b = COASTLINE[i + 1];
    const zMin = Math.min(a[1], b[1]);
    const zMax = Math.max(a[1], b[1]);
    if (z < zMin || z > zMax) continue;
    const t = zMax - zMin < 1e-6 ? 0 : (z - a[1]) / (b[1] - a[1]);
    const boundaryX = lerp(a[0], b[0], t);
    if (x >= boundaryX) return true;
  }
  return false;
}

/** How far the point sits inside the theatre border, 0 at the edge and 1 well inside. */
function borderMask(x: number, z: number): number {
  const half = WORLD.sizeM * 0.5;
  const edge = Math.min(half - Math.abs(x), half - Math.abs(z));
  return smoothstep(0, THEATRE.borderFadeM, edge);
}

/**
 * Height of the theatre at a world position, metres. Pure: the same seed and position always
 * give the same height, on any machine, which is what makes the committed binary meaningful.
 */
export function keraniHeightM(x: number, z: number, noise: SimplexNoise): number {
  const inland = inlandDistanceM(x, z, noise);

  // --- Bathymetry: a smooth shelf dropping away from the beach. Deliberately free of fine
  // detail; see smoothInlandDistanceM.
  if (inland <= 0) {
    const seaward = -smoothInlandDistanceM(x, z, noise);
    if (seaward > 0) {
      const shelf = smoothstep(0, THEATRE.shelfWidthM, seaward);
      const swell = noise.fbm(x * 0.00019, z * 0.00019, 2) * 16;
      return -THEATRE.shelfDepthM * shelf + swell * shelf;
    }
    // Inside the detailed waterline but outside the smooth one: the shallow surf margin.
    return inland * 0.05;
  }

  // --- Beach, then coastal plain.
  const beach = smoothstep(0, THEATRE.beachWidthM, inland);
  const plain = smoothstep(THEATRE.beachWidthM, THEATRE.plainWidthM, inland);
  let height = beach * 4 + plain * THEATRE.plainHeightM;

  // --- Rolling ground everywhere on land, warped so the drainage bends.
  const rolling = noise.warpedFbm(x * 0.00068, z * 0.00068, 5, 1.1);
  height += rolling * 95 * plain;

  // --- Mountain spine: ridged multifractal, masked to a band along the spine polyline.
  const spineDistance = distanceToPolyline(x, z, SPINE);
  const spineMask = smoothstep(THEATRE.spineHalfWidthM, THEATRE.spineHalfWidthM * 0.18, spineDistance);
  if (spineMask > 0) {
    const ridge = noise.ridged(x * 0.00034, z * 0.00034, THEATRE.spineOctaves);
    // Crag modulation is kept coarse and shallow on purpose: multiplied against a 1400 m
    // spine, a high-frequency term here injects hundreds of metres of relief at wavelengths the
    // 11.7 m grid cannot carry, which aliases into the silhouette and dominates the file size.
    const crags = noise.fbm(x * 0.0021, z * 0.0021, 3) * 0.5 + 0.5;
    height += THEATRE.spineHeightM * spineMask * ridge * lerp(0.82, 1, crags);
  }

  // --- River valley: carve a channel and flatten its floor toward the water line.
  const riverDistance = distanceToPolyline(x, z, RIVER) + noise.fbm(x * 0.0009, z * 0.0009, 3) * 160;
  const riverMask = smoothstep(THEATRE.riverHalfWidthM * 4.5, THEATRE.riverHalfWidthM, riverDistance);
  if (riverMask > 0) {
    const floor = smoothstep(0, 9000, Math.max(0, x - 2400)) * 120 + 2;
    height = lerp(height, Math.min(height, floor), riverMask * 0.92);
    height -= THEATRE.riverDepthM * riverMask * 0.35;
  }

  // --- Sand spit: a low bar across the bay mouth, the theatre's most recognisable landmark.
  const spitDistance = distanceToPolyline(x, z, SPIT);
  const spitMask = smoothstep(THEATRE.spitHalfWidthM * 3.2, THEATRE.spitHalfWidthM, spitDistance);
  if (spitMask > 0) {
    height = Math.max(height, lerp(-3, THEATRE.spitHeightM, spitMask));
  }

  // --- Port town: a flat shelf, so the settlement generator has somewhere sane to build.
  const townDistance = Math.hypot(x - THEATRE.townCentre[0], z - THEATRE.townCentre[1]);
  const townMask = smoothstep(THEATRE.townRadiusM * 1.7, THEATRE.townRadiusM * 0.6, townDistance);
  if (townMask > 0) {
    height = lerp(height, THEATRE.townHeightM, townMask * 0.88);
  }

  // --- Fade the landmass into the sea at the theatre border, so the edge is water rather than
  // a cliff clamped to infinity by the sampler.
  const border = borderMask(x, z);
  height = lerp(-THEATRE.shelfDepthM * 0.55, height, border);

  return height;
}

/** Build the full heightfield. Row-major, `resolution²` samples, metres. */
export function generateKeraniHeights(resolution: number): Float32Array {
  const noise = new SimplexNoise(THEATRE.seed);
  const heights = new Float32Array(resolution * resolution);
  const step = WORLD.sizeM / (resolution - 1);
  const origin = -WORLD.sizeM * 0.5;

  for (let row = 0; row < resolution; row += 1) {
    const z = origin + row * step;
    const rowStart = row * resolution;
    for (let column = 0; column < resolution; column += 1) {
      const x = origin + column * step;
      heights[rowStart + column] = keraniHeightM(x, z, noise);
    }
  }
  return heights;
}
